import { z } from "@reflag/shared/utils/zod";

import {
  APICollectionResponseWithMetadata,
  APIEmptyResponse,
  APIResponse,
} from "./api";
import { AppHeaderDTO, CreateAppWithOrgArgsSchema } from "./appAPI";
import { MemberRole, MemberRoleSchema } from "./permissions";
import { nameSchema } from "./schemas/nameSchema";
import { KeyFormat, KeyFormatSchema } from "./utils/keyFormat";

const orgNameSchema = nameSchema.max(40, {
  message: "Organization name must be 40 characters or less",
});

const nullableStringField = z.preprocess(
  (value) => (value === "" ? null : value),
  z.string().nullable(),
);

export const organizationLogoUrlSchema = z.preprocess(
  (value) => (value === "" ? null : value),
  z
    .string()
    .url({ abort: true })
    .refine(
      (value) => new URL(value).pathname.startsWith("/api/images/org-logos/"),
      "Invalid organization logo URL",
    )
    .nullable(),
);

export const CreateOrgArgsSchema = z
  .object({
    name: orgNameSchema,
    logoUrl: organizationLogoUrlSchema.nullish(),
    app: CreateAppWithOrgArgsSchema,
  })
  .strict();

export type CreateOrgArgsType = z.infer<typeof CreateOrgArgsSchema>;
export const PatchOrgArgsSchema = CreateOrgArgsSchema.pick({
  name: true,
  logoUrl: true,
})
  .extend({
    featureKeyFormat: KeyFormatSchema,
    archiveAfterLastCheckInDays: z.number().min(0),
    archiveAfterRolloutInDays: z.number().min(0),
    autoArchive: z.boolean(),
    autoCreateLinearCleanupIssue: z.boolean(),
    createLinearCleanupIssuesForExistingStaleFlags: z.boolean(),
    linearCleanupIssueTeamId: nullableStringField,
    linearCleanupIssueTemplateId: z.union([
      z.string().uuid(),
      z.literal("").transform(() => null),
      z.null(),
    ]),
    defaultMemberRole: MemberRoleSchema,
  })
  .partial();

export type PatchOrgArgsType = z.input<typeof PatchOrgArgsSchema>;

/**
 * This should be kept in sync with the prisma schema at all times
 */
export const AccessLevels = [
  "starter",
  "pro",
  "business",
  "enterprise",
] as const;
export const AccessLevelSchema = z.enum(AccessLevels);
export type AccessLevel = z.infer<typeof AccessLevelSchema>;

export type InviteDTO = {
  inviteKey: string;
};

export type OrganizationDomainDTO = {
  id: string;
  domain: string;
  verifiedAt: string;
};

export const CreateOrganizationDomainSchema = z
  .object({
    domain: z.string().trim().min(1).max(255),
  })
  .strict();

export type CreateOrganizationDomainType = z.infer<
  typeof CreateOrganizationDomainSchema
>;

export type Org = {
  id: string;
  name: string;
  logoUrl: string | null;
  apps: AppHeaderDTO[];
  createdAt: string;
  trialEndsAt: string | null;
  suspendedAt: string | null;
  accessLevel: AccessLevel;
  defaultMemberRole: MemberRole;
  isGlobal: boolean;
  featureKeyFormat: KeyFormat;
  userIsMember: boolean;
  archiveAfterLastCheckInDays: number;
  archiveAfterRolloutInDays: number;
  userRole: MemberRole;
  autoArchive: boolean;
  autoCreateLinearCleanupIssue: boolean;
  linearCleanupIssueTeamId: string | null;
  linearCleanupIssueTemplateId: string | null;
  integrations: {
    slack: boolean;
    linear: boolean;
    github: boolean;
  };
  hasStripeCustomerId: boolean;
};

export type OrgHeader = Omit<Org, "apps"> & {
  apps: AppHeaderDTO[];
};

export type Member = {
  id: string;
  name: string;
  email: string;
  signedUpAt: string;
  joinedAt: string;
  avatarUrl: string | null;
  role: MemberRole;
};

export const PatchMemberSchema = z.object({
  role: MemberRoleSchema,
});

export type PatchMemberType = z.infer<typeof PatchMemberSchema>;

export type TrackingUsage = {
  month: string;
  trackedUsers: number;
};

export const BillingPortalQuerySchema = z.object({
  returnUrl: z.string().url(),
});

export type BillingPortalQueryType = z.infer<typeof BillingPortalQuerySchema>;

export const CreateCheckoutSessionSchema = z.object({
  successUrl: z.url(),
  cancelUrl: z.url(),
  paymentTerms: z.enum(["monthly", "yearly"]),
});

export type CreateCheckoutSessionType = z.infer<
  typeof CreateCheckoutSessionSchema
>;

export interface OrgAPI {
  "/orgs": {
    GET: {
      response: APICollectionResponseWithMetadata<
        OrgHeader,
        {
          orgsWithUserDomainExists: boolean;
          userDomainAutoJoinEligible: boolean;
        }
      >;
    };
    POST: {
      response: APIResponse<{
        org: Org;
      }>;
      body: CreateOrgArgsType;
    };
  };
  "/orgs/:orgId/linear-cleanup-issue-candidates": {
    GET: {
      response: APIResponse<{ count: number }>;
      params: { orgId: string };
    };
  };
  "/orgs/:orgId": {
    PATCH: {
      response: APIResponse<{
        org: Org;
      }>;
      params: {
        orgId: string;
      };
      body: PatchOrgArgsType;
    };
  };
  "/orgs/:orgId/logo": {
    POST: {
      response: APIResponse<{ logoUrl: string }>;
      params: { orgId: string };
      body: ArrayBuffer;
    };
  };
  "/orgs/:orgId/domains": {
    GET: {
      response: APIResponse<{
        domains: OrganizationDomainDTO[];
        candidateDomains: string[];
      }>;
      params: {
        orgId: string;
      };
    };
    POST: {
      response: APIResponse<{
        domain: OrganizationDomainDTO;
      }>;
      params: {
        orgId: string;
      };
      body: CreateOrganizationDomainType;
    };
  };
  "/orgs/:orgId/domains/:domainId": {
    DELETE: {
      response: APIEmptyResponse;
      params: {
        orgId: string;
        domainId: string;
      };
    };
  };
  "/orgs/:orgId/members": {
    GET: {
      response: APIResponse<Member[]>;
      params: {
        orgId: string;
      };
    };
  };
  "/orgs/:orgId/members/:memberId": {
    PATCH: {
      response: APIResponse<{
        member: Member;
      }>;
      params: {
        orgId: string;
        memberId: string;
      };
      body: PatchMemberType;
    };
    DELETE: {
      response: APIEmptyResponse;
      params: {
        orgId: string;
        memberId: string;
      };
    };
  };
  "/orgs/:orgId/invite/refresh": {
    POST: {
      response: APIResponse<{
        invite: InviteDTO;
      }>;
      params: {
        orgId: string;
      };
    };
  };
  "/orgs/:orgId/invite": {
    GET: {
      response: APIResponse<{
        invite: InviteDTO;
      }>;
      params: {
        orgId: string;
      };
    };
  };
  "/orgs/:orgId/monthly-tracked-users": {
    GET: {
      response: APIResponse<Array<TrackingUsage>>;
      params: {
        orgId: string;
      };
    };
  };
  "/orgs/:orgId/billing-portal": {
    GET: {
      response: APIResponse<undefined>;
      params: {
        orgId: string;
      };
      query: BillingPortalQueryType;
    };
  };
  "/orgs/:orgId/checkout-session": {
    POST: {
      response: APIResponse<{
        sessionUrl: string;
      }>;
      params: {
        orgId: string;
      };
      body: CreateCheckoutSessionType;
    };
  };
}
